import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message, Space, Divider, Row, Col, Card, Checkbox, InputNumber, Upload } from "antd";
import { MinusCircleOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, AppstoreOutlined } from "@ant-design/icons";
import ProductServ from "../service/ProductServ";
import ProductOptionServ from "../service/ProductOptionServ";
import GlobalButton from "../util/GlobalButton";
import { getImageUrl } from "./SafeImage";
import { normalizeVariantType } from "../util/unitFormatter";
import VariantConfigurationEngine from "./VariantEngine/VariantConfigurationEngine";
import { validateVariantGroup } from "../util/variantValidation";
import { recalculateOptionsPricing } from "../util/variantUtils";

const { Option } = Select;

const AddProductOption = ({ open, refreshOptions, optionDetails, isEditing }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Canonical variant types — no legacy values
  const [optionTypes, setOptionTypes] = useState(['Kilograms (kg)', 'Liters (L)', 'Size', 'Pack', 'Pieces (pcs)']);
  const [submitting, setSubmitting] = useState(false);

  // Fetch products and option types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const role = localStorage.getItem("role");
        let prodResponse;
        if (role === "seller") {
          prodResponse = await ProductServ.getSellerProducts({ limit: "all" });
        } else {
          prodResponse = await ProductServ.index({ limit: "all" });
        }
        setProducts(prodResponse?.data || []);

        // Fetch unique option names for the dropdown.
        // Normalize any legacy names from DB before merging to prevent
        // 'Weight'/'Volume' from re-appearing in the dropdown.
        const optResponse = await ProductOptionServ.index({ limit: 'all' });
        const CANONICAL = new Set(['Kilograms (kg)', 'Liters (L)', 'Size', 'Pack', 'Pieces (pcs)']);
        const fetchedNames = (optResponse?.data || []).map(o => normalizeVariantType(o.name));
        const uniqueNames = [...new Set([...optionTypes, ...fetchedNames])].filter(n => CANONICAL.has(n));
        setOptionTypes(uniqueNames.length > 0 ? uniqueNames : [...CANONICAL]);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  const onProductChange = async (productId) => {
    if (!productId) return;
    try {
      // 1. Try to find product in local list for quick UI feedback
      let prod = products.find(p => (p._id || p.id) === productId);
      
      // 2. Fetch full details from server
      const response = await ProductServ.single(productId, { isManagement: true });
      const details = response.data || response;
      
      // If not in local list (e.g. on direct edit), use fetched details
      if (!prod) prod = details;
      setSelectedProduct(prod);

      if (details.options && details.options.length > 0) {
        // ✅ Normalize legacy option names before populating the form
        const normalizedOptions = details.options.map(opt => ({
          ...opt,
          name: normalizeVariantType(opt.name),
        }));
        const { newOptions } = recalculateOptionsPricing(normalizedOptions);
        form.setFieldsValue({ options: newOptions });
      } else if (!isEditing) {
        const defaultOptions = [{
          name: 'Kilograms (kg)',
          autoCalculate: true,
          variants: [{
            value: '',
            price: details.price,
            mrp: details.mrp,
            stock: details.stock,
            discount: details.discount || 0
          }]
        }];
        const { newOptions } = recalculateOptionsPricing(defaultOptions);
        form.setFieldsValue({ options: newOptions });
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load product details");
    }
  };

  // Set form values if editing
  useEffect(() => {
    if (isEditing && optionDetails) {
      const pId = optionDetails.productId?._id || optionDetails.productId;
      form.setFieldsValue({
        productId: pId,
        options: optionDetails.options || []
      });
      // Trigger initialization logic
      if (pId) {
        onProductChange(pId);
      }
    } else {
      form.resetFields();
      setSelectedProduct(null);
    }
  }, [optionDetails, isEditing, form, products.length]); // Added products.length to ensure p find works

  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      // Normalize values before saving
      const normalizeVariants = (variants, groupName) => {
        if (!variants) return [];
        return variants.map(v => {
          let val = (v.value || "").toString();
          if (/^\d+(\.\d+)?$/.test(val)) {
            const gName = (groupName || "").toLowerCase();
            // New labels
            if (gName.includes('kilogram') || gName.includes('kg')) val += 'g';
            else if (gName.includes('liter') || gName.includes('litre') || gName.includes('liters (l)')) val += 'ml';
            else if (gName.includes('pieces') || gName.includes('pcs')) val += ' pcs';
            // Backward compat with old labels
            else if (gName.includes('weight')) val += ' grams';
            else if (gName.includes('volume')) val += 'ml';
          }
          return { ...v, value: val };
        });
      };

      const normalizedOptions = (values.options || []).map(opt => ({
        ...opt,
        variants: normalizeVariants(opt.variants, opt.name)
      }));

      const payload = {
        options: normalizedOptions,
      };

      await ProductServ.update(values.productId, payload);
      message.success("Product Options Synchronized!");

      handleClose(true);
    } catch (err) {
      console.error(err);
      message.error(err.message || "Error saving product option!");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (shouldRefresh) => {
    refreshOptions(shouldRefresh);
    form.resetFields();
  };


  return (
    <Modal
      open={open}
      onCancel={() => handleClose(false)}
      width={800}
      centered={true}
      title={null}
      closable={false}
      footer={null}
      styles={{ body: { padding: 0 } }}
      bodyStyle={{ padding: 0 }}
      style={{ borderRadius: '16px', overflow: 'hidden' }}
    >
      <style>{`
        .variant-image-upload .ant-upload.ant-upload-select,
        .variant-image-upload .ant-upload-wrapper .ant-upload-select {
            width: auto !important;
            height: auto !important;
            border: none !important;
            background: transparent !important;
            margin: 0 !important;
            border-radius: 0 !important;
        }
      `}</style>
      {/* Premium Header Layout matching Image 2 */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "18px 24px",
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "42px",
            height: "42px",
            background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)"
          }}>
            <AppstoreOutlined style={{ fontSize: "20px", color: "#4f46e5" }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#1e293b", letterSpacing: "-0.3px" }}>
              {isEditing ? "Modify Product Details" : "Configure Product Options"}
            </h1>
            <p style={{ margin: 0, fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
              Standardized Cross-Tab Validation Active
            </p>
          </div>
        </div>
        <div 
          onClick={() => handleClose(false)} 
          style={{ 
            fontSize: '16px', 
            color: '#64748b', 
            cursor: 'pointer', 
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
        >
          ✕
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Modal Scrollable Content Area */}
        <div style={{ padding: "24px", maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}>
                  {/* Horizontal Side-by-Side Split Panel */}
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            {/* Left Side: Variant Configuration Engine Info */}
            <div style={{ flex: 1.1 }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                ⚙️ Variant Configuration Engine
              </h3>
              <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
                Create custom groups for Kilograms (kg), Liters (L), Size, Pack, or Pieces (pcs). Instantly auto-calculate variant prices, discounts, and stocks based on basic specifications.
              </p>
            </div>

            {/* Right Side: Product Selection and Compact Preview */}
            <div style={{ flex: 0.9 }}>
              <Form.Item
                label={<span style={{ fontWeight: 800, color: '#475569', fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Product Selection</span>}
                name="productId"
                rules={[{ required: true, message: "Select a product" }]}
                style={{ marginBottom: selectedProduct ? '10px' : '0' }}
              >
                <Select
                  placeholder="Search and select product"
                  showSearch
                  onChange={onProductChange}
                  optionLabelProp="label"
                  optionFilterProp="title"
                  size="middle"
                  style={{ width: '100%', borderRadius: '8px' }}
                >
                  {products.map((p) => (
                    <Select.Option
                      key={p._id || p.id}
                      value={p._id || p.id}
                      title={p.name}
                      label={
                        <div className="pcu-flex pcu-flex-center-y pcu-gap-10">
                          <img
                            src={getImageUrl(p.imageUrl)}
                            alt={p.name}
                            className="pcu-w-24px pcu-h-24px pcu-radius-4 pcu-object-cover"
                          />
                          <span className="pcu-font-bold">{p.name}</span>
                        </div>
                      }
                    >
                      <div className="pcu-flex pcu-flex-center-y pcu-gap-12 pcu-padding-y-4">
                        <img
                          src={getImageUrl(p.imageUrl)}
                          alt={p.name}
                          className="pcu-w-45px pcu-h-45px pcu-radius-8 pcu-object-cover pcu-border-light"
                        />
                        <div className="pcu-flex-1">
                          <div className="pcu-font-bold pcu-color-text-header pcu-font-14">{p.name}</div>
                          <div className="pcu-font-12 pcu-color-muted">
                            {p.shopName || "Seller Shop"}
                          </div>
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedProduct && (
                <Card 
                  size="small" 
                  styles={{ body: { padding: '6px 10px' } }}
                  bodyStyle={{ padding: '6px 10px' }}
                  style={{ 
                    background: '#ffffff', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.01)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img
                      src={getImageUrl(selectedProduct.imageUrl)}
                      alt="Selected"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0',
                        background: '#fff'
                      }}
                    />
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>
                          {selectedProduct.name}
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>
                          Price: ₹{selectedProduct.price} • Stock: {selectedProduct.stock}
                        </div>
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#FF8800', background: '#fff7ed', padding: '1px 6px', borderRadius: '4px' }}>
                        {selectedProduct.shopName}
                      </span>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>

          {/* Variants Form List */}
          <Form.Item 
            name="options"
            rules={[
              {
                validator: async (_, options) => {
                  if (!options || options.length === 0) return Promise.resolve();
                  for (const group of options) {
                    const groupErrs = validateVariantGroup(group);
                    if (groupErrs.length > 0) {
                      return Promise.reject(new Error(groupErrs[0]));
                    }
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <VariantConfigurationEngine categoryName={selectedProduct?.category?.name || ""} />
          </Form.Item>
        </div>

        {/* Premium Bottom Footer Layout matching Image 2 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          background: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          borderBottomLeftRadius: '16px',
          borderBottomRightRadius: '16px'
        }}>
          {/* Left Side: SECURE SYNC Badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', fontWeight: 800, fontSize: '11px', letterSpacing: '0.8px' }}>
            <span style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>🛡</span> SECURE SYNC
          </div>

          {/* Right Side: Action Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              onClick={() => handleClose(false)}
              disabled={submitting}
              style={{
                height: '38px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '12px',
                color: '#64748b',
                background: '#f1f5f9',
                border: 'none',
                padding: '0 16px'
              }}
            >
              CANCEL
            </Button>
            
            <Button
              onClick={() => handleClose(false)}
              disabled={submitting}
              style={{
                height: '38px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '12px',
                color: '#64748b',
                background: '#f1f5f9',
                border: 'none',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              ← BACK
            </Button>

            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={submitting}
              style={{
                height: '38px',
                borderRadius: '8px',
                fontWeight: 800,
                fontSize: '12px',
                background: 'linear-gradient(135deg, #ff5e00 0%, #ff782e 100%)',
                border: 'none',
                color: '#fff',
                padding: '0 20px',
                boxShadow: '0 4px 12px rgba(255, 94, 0, 0.2)'
              }}
            >
              UPDATE PRODUCT
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default AddProductOption;
